import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { AuthGuard } from "../App";
import { EmailGuard } from "../App";
import { Oval } from "react-loader-spinner"; // Import loader
import { FaBuilding, FaMapMarkerAlt, FaFeatherAlt, FaChevronLeft, FaPenFancy, FaGlobe } from 'react-icons/fa'; // Import icons
import { StandardURL } from "../App";

const UpdateProperty = () => {
    // Note: selectedPropertyId from useParams is unused, the code relies on localStorage as original.
    const { selectedPropertyId } = useParams();
    const navigate = useNavigate();
    
    // States for form fields and UI/loading
    const [property, setProperty] = useState(null);
    const [title, setTitle] = useState("");
    const [description, setDescription] = useState("");
    const [location, setLocation] = useState("");
    const [county, setCounty] = useState("");
    const [isFetching, setIsFetching] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Data from localStorage (as per original logic)
    const PropertyId = localStorage.getItem("selectedPropertyId");
    const token = localStorage.getItem("token");

    // Fetch property details
    useEffect(() => {
        if (!token || !PropertyId) {
            toast.error("Missing authentication token or property ID.");
            setIsFetching(false);
            return;
        }

        setIsFetching(true);
        axios
            .get(
                `${StandardURL}/iNyumba/admin/properties/records/${PropertyId}`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            )
            .then((response) => {
                const { title, description, location, county } = response.data.response;
                setProperty(response.data.response);
                setTitle(title || "");
                setDescription(description || "");
                setLocation(location || "");
                setCounty(county || "");
            })
            .catch((error) => {
                toast.error("Failed to fetch property details.");
                console.error("Error fetching property:", error);
            })
            .finally(() => {
                setIsFetching(false);
            });
    }, [selectedPropertyId, PropertyId, token]); // Include dependencies for completeness

    // Handle form submission (Update)
    const handleSubmit = (e) => {
        e.preventDefault();

        if (!token || !PropertyId) {
            toast.error("Cannot update: Authentication token or property ID is missing.");
            return;
        }
        
        setIsSubmitting(true);

        axios
            .patch(
                `${StandardURL}/iNyumba/admin/properties/update/${PropertyId}`,
                {
                    title,
                    description,
                    location,
                    county,
                },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            )
            .then((response) => {
                toast.success(response.data.response);
                // navigate(-1) is handled after successful action
            })
            .catch((error) => {
                const errorMessage = error.response?.data?.response || "Failed to update property.";
                toast.error(errorMessage);
                console.error("Error updating property:", error);
            })
            .finally(() => {
                setIsSubmitting(false);
                navigate(-1); // Go back one step in navigation (Original functionality)
            });
    };
    
    const handleGoBack = () => navigate(-1);

    if (isFetching) {
        return (
            <div className="flex justify-center items-center min-h-screen bg-gray-50">
                <Oval
                    height={50}
                    width={50}
                    color="#3b82f6"
                    secondaryColor="#93c5fd"
                    ariaLabel="loading-indicator"
                />
                <p className="ml-3 text-lg text-gray-700">Loading property details...</p>
            </div>
        );
    }
    
    // Check if property details were successfully loaded
    if (!property) {
        return (
            <div className="text-center p-10 mt-10 bg-white shadow-lg mx-auto max-w-lg rounded-xl">
                <p className="text-xl font-semibold text-red-600">Property Not Found</p>
                <p className="text-gray-500 mt-2">Could not load details for property ID: {PropertyId || 'N/A'}.</p>
                <button
                    onClick={handleGoBack}
                    className="mt-5 text-gray-600 hover:text-blue-600 transition duration-150 flex items-center justify-center mx-auto"
                >
                    <FaChevronLeft className="mr-1 w-4 h-4" /> Go Back
                </button>
            </div>
        );
    }

    return (
        <EmailGuard>
            <AuthGuard>
                <div className="min-h-screen bg-gray-50 flex justify-center p-4 sm:p-6 lg:p-8">
                    <div className="w-full max-w-2xl bg-white p-8 rounded-xl shadow-2xl border border-gray-100">
                        
                        {/* Header and Back Button */}
                        <div className="mb-8 border-b pb-4 flex justify-between items-center">
                            <h1 className="text-3xl font-extrabold text-gray-900 flex items-center">
                                <FaPenFancy className="text-indigo-600 mr-3" /> Update Property Details
                            </h1>
                            <button
                                onClick={handleGoBack}
                                className="text-gray-600 hover:text-blue-600 transition duration-150 flex items-center text-sm font-medium"
                            >
                                <FaChevronLeft className="mr-1 w-4 h-4" /> Go Back
                            </button>
                        </div>

                        {/* Property ID Display */}
                        <div className="bg-blue-50 border-l-4 border-blue-400 text-blue-800 p-4 mb-6 rounded">
                            <p className="font-semibold">
                                Currently editing property with ID: <span className="font-extrabold">{PropertyId}</span>
                            </p>
                        </div>
                        
                        {/* Form */}
                        <form onSubmit={handleSubmit} className="space-y-6">
                            
                            {/* Title */}
                            <div>
                                <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                                    <FaBuilding className="mr-2 text-indigo-500" /> Property Title
                                </label>
                                <input
                                    type="text"
                                    id="title"
                                    value={title}
                                    onChange={(e) => setTitle(e.target.value)}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-indigo-500 focus:border-indigo-500 transition duration-150"
                                    required
                                />
                            </div>

                            {/* Description */}
                            <div>
                                <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                                    <FaFeatherAlt className="mr-2 text-indigo-500" /> Description
                                </label>
                                <textarea
                                    id="description"
                                    rows="4"
                                    value={description}
                                    onChange={(e) => setDescription(e.target.value)}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-indigo-500 focus:border-indigo-500 transition duration-150"
                                    required
                                />
                            </div>

                            {/* Location */}
                            <div>
                                <label htmlFor="location" className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                                    <FaMapMarkerAlt className="mr-2 text-indigo-500" /> Location (Street/Area)
                                </label>
                                <input
                                    type="text"
                                    id="location"
                                    value={location}
                                    onChange={(e) => setLocation(e.target.value)}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-indigo-500 focus:border-indigo-500 transition duration-150"
                                    required
                                />
                            </div>

                            {/* County */}
                            <div>
                                <label htmlFor="county" className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                                    <FaGlobe className="mr-2 text-indigo-500" /> County
                                </label>
                                <input
                                    type="text"
                                    id="county"
                                    value={county}
                                    onChange={(e) => setCounty(e.target.value)}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-indigo-500 focus:border-indigo-500 transition duration-150"
                                    required
                                />
                            </div>
                            
                            {/* Submit Button */}
                            <button
                                type="submit"
                                disabled={isSubmitting}
                                className={`w-full inline-flex items-center justify-center font-bold py-3 px-4 rounded-lg shadow-lg transition duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 
                                    ${isSubmitting
                                        ? "bg-gray-500 cursor-not-allowed"
                                        : "bg-indigo-600 hover:bg-indigo-700 text-white focus:ring-indigo-500"
                                    } disabled:opacity-80`}
                            >
                                {isSubmitting ? (
                                    <>
                                        <Oval
                                            height={20}
                                            width={20}
                                            color="white"
                                            visible={true}
                                            ariaLabel="loading-indicator"
                                            secondaryColor="#ffffff"
                                            strokeWidth={5}
                                        />
                                        <span className="ml-2">Updating Property...</span>
                                    </>
                                ) : (
                                    "Save Updated Property Details"
                                )}
                            </button>
                        </form>
                    </div>
                </div>
            </AuthGuard>
        </EmailGuard>
    );
};

export default UpdateProperty;